/**
 * Click-to-call button — works for any authenticated staff role.
 *
 * Click → backend dials patient via Vonage → on answer, bridges to the
 * staff member's phone (from their user profile). If Vonage isn't yet
 * configured the button still works as a stub (records intent + audit
 * trail) so the UX can be tested ahead of provisioning.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Phone, PhoneCall, AlertCircle } from "lucide-react";
import API from "@/lib/api";
import { toast } from "sonner";

/**
 * Props:
 *   patientId        string  — required. Patient user id.
 *   patientLabel     string  — optional, displayed in toast ("Patient John D.")
 *   chatThreadId     string  — optional, attaches the call to a chat thread.
 *   compact          bool    — small icon-only button (for chat headers)
 *   variant          string  — shadcn variant pass-through (default "outline")
 *   className        string
 */
export default function ClickToCallButton({
  patientId,
  patientLabel,
  chatThreadId,
  compact = false,
  variant = "outline",
  className = "",
}) {
  const [calling, setCalling] = useState(false);

  const dial = async () => {
    setCalling(true);
    try {
      const { data } = await API.post("/voice/click-to-call", {
        patient_id: patientId,
        chat_thread_id: chatThreadId || null,
      });
      if (data.stub) {
        toast.info(
          `Stub: Vonage not configured yet. Call to ${patientLabel || "patient"} recorded as intent only.`,
          { duration: 5000 }
        );
      } else {
        toast.success(
          `📞 Dialing ${patientLabel || "patient"} — your phone will ring shortly to connect.`,
          { duration: 6000 }
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("No phone on your staff profile")) {
        toast.error("Add your phone in Profile before using click-to-call");
      } else if (typeof detail === "string" && detail.includes("no phone on file")) {
        toast.error("Patient has no phone on file");
      } else {
        toast.error(detail || "Call failed — try again");
      }
    } finally {
      setCalling(false);
    }
  };

  if (compact) {
    return (
      <Button
        type="button"
        size="icon"
        variant={variant}
        disabled={calling || !patientId}
        onClick={dial}
        title={`Call ${patientLabel || "patient"}`}
        aria-label="Click to call patient"
        className={`h-8 w-8 ${className}`}
        data-testid="click-to-call-btn-compact"
      >
        {calling ? <PhoneCall className="w-4 h-4 animate-pulse" /> : <Phone className="w-4 h-4" />}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant={variant}
      disabled={calling || !patientId}
      onClick={dial}
      className={`gap-1.5 ${className}`}
      data-testid="click-to-call-btn"
    >
      {calling ? (
        <>
          <PhoneCall className="w-4 h-4 animate-pulse" /> Dialing…
        </>
      ) : (
        <>
          <Phone className="w-4 h-4" /> Call patient
        </>
      )}
    </Button>
  );
}

/** Banner version for when patient has no phone — UI hint, not a call action. */
export function ClickToCallUnavailable() {
  return (
    <div
      className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 px-2 py-1 rounded-md bg-slate-50 dark:bg-slate-800/40"
      data-testid="click-to-call-unavailable"
    >
      <AlertCircle className="w-3 h-3" /> No phone on file
    </div>
  );
}
